import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Select } from '@saas-kit/ui';
import {
  DataTable,
  type DataTableColumn,
  type PaginationState,
} from '@saas-kit/ui/DataTable';

import {
  MEMBERSHIP_ROLES,
  type Member,
  type MembershipRole,
  type TenantRole,
} from './api';
import { MemberRolesCell } from './MemberRolesCell';

export type MemberActionKind = 'suspend' | 'remove' | 'reactivate';

interface MembersTableProps {
  members: Member[];
  roles: TenantRole[];
  canManage: boolean;
  roleBusy: boolean;
  rolesBusy: boolean;
  onChangeRole: (membershipId: string, role: MembershipRole) => void;
  onAssignRole: (membershipId: string, roleId: string) => void;
  onRevokeRole: (membershipId: string, roleId: string) => void;
  onAction: (kind: MemberActionKind, member: Member) => void;
}

const DEFAULT_PAGE_SIZE = 25;

/** The workspace members table — base role, custom roles, and status actions.
 *  Rendered with the shared DataTable: selectable rows plus client-side
 *  pagination (the /memberships endpoint returns the full list at once). */
export function MembersTable({
  members,
  roles,
  canManage,
  roleBusy,
  rolesBusy,
  onChangeRole,
  onAssignRole,
  onRevokeRole,
  onAction,
}: MembersTableProps): JSX.Element {
  const { t } = useTranslation('members');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const columns = useMemo<DataTableColumn<Member>[]>(
    () => [
      {
        id: 'name',
        header: t('list.columns.name'),
        cell: (member) => member.full_name ?? '—',
      },
      {
        id: 'email',
        header: t('list.columns.email'),
        cell: (member) => member.email,
      },
      {
        id: 'role',
        header: t('list.columns.role'),
        cell: (member) => (
          <Select
            value={member.role}
            disabled={!canManage || roleBusy}
            onChange={(event) =>
              onChangeRole(member.id, event.target.value as MembershipRole)
            }
          >
            {MEMBERSHIP_ROLES.map((role) => (
              <option key={role} value={role}>
                {t(`roles.${role}`)}
              </option>
            ))}
          </Select>
        ),
      },
      {
        id: 'roles',
        header: t('list.columns.roles'),
        cell: (member) => (
          <MemberRolesCell
            member={member}
            roles={roles}
            canManage={canManage}
            busy={rolesBusy}
            onAssign={(roleId) => onAssignRole(member.id, roleId)}
            onRevoke={(roleId) => onRevokeRole(member.id, roleId)}
          />
        ),
      },
      {
        id: 'status',
        header: t('list.columns.status'),
        cell: (member) => (
          <Badge variant={member.status === 'active' ? 'outline' : 'secondary'}>
            {t(`status.${member.status}`)}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: t('list.columns.actions'),
        align: 'end',
        cell: (member) => (
          <div className="flex flex-wrap justify-end gap-2">
            {!canManage ? (
              <span className="text-xs text-muted-foreground">
                {t('list.read_only')}
              </span>
            ) : member.status === 'active' ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onAction('suspend', member)}
                >
                  {t('actions.suspend')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onAction('remove', member)}
                >
                  {t('actions.remove')}
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => onAction('reactivate', member)}>
                {t('actions.reactivate')}
              </Button>
            )}
          </div>
        ),
      },
    ],
    [
      t,
      roles,
      canManage,
      roleBusy,
      rolesBusy,
      onChangeRole,
      onAssignRole,
      onRevokeRole,
      onAction,
    ],
  );

  const pageCount = Math.max(1, Math.ceil(members.length / pageSize));
  const safeIndex = Math.min(pageIndex, pageCount - 1);
  const pageRows = useMemo(
    () => members.slice(safeIndex * pageSize, safeIndex * pageSize + pageSize),
    [members, safeIndex, pageSize],
  );

  const pagination: PaginationState = {
    pageIndex: safeIndex,
    pageSize,
    hasNext: safeIndex + 1 < pageCount,
    hasPrev: safeIndex > 0,
    onNext: () => setPageIndex(safeIndex + 1),
    onPrev: () => setPageIndex(Math.max(0, safeIndex - 1)),
    onPageSizeChange: (n) => {
      setPageSize(n);
      setPageIndex(0);
    },
  };

  return (
    <DataTable
      testId="members-table"
      data={pageRows}
      columns={columns}
      rowKey={(member) => member.id}
      selectable
      pagination={pagination}
      emptyText={t('list.empty')}
    />
  );
}
